import React, { useState, useEffect } from "react";
import AddPageModal from "./AddPageModal";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";

const MetaManager = () => {
  const [pages, setPages] = useState<any[]>([]);
  const [selectedPage, setSelectedPage] = useState("");
  const [message, setMessage] = useState("");
  const [media, setMedia] = useState<File | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [status, setStatus] = useState("idle"); // 'idle' | 'loading' | 'success' | 'error'
  const [feedback, setFeedback] = useState("");
  const [clientId, setClientId] = useState("");
  const [serverAuthToken, setServerAuthToken] = useState("");
  const [apiEndpoint, setApiEndpoint] = useState("");
  // 1. Fetch available pages on load
  useEffect(() => {
    const fetchPages = async () => {
      try {
        // TODO: Replace with your actual proxy server endpoint to get connected pages
        // const response = await fetch('/api/fb-pages');
        // const data = await response.json();

        // Dummy data for UI building
        const mockPages: any = [
          { id: "123456789", name: "Acme Corp Official" },
          { id: "987654321", name: "Acme Support" },
        ];

        setPages(mockPages);
        if (mockPages.length > 0) setSelectedPage(mockPages[0].id);
      } catch (err) {
        console.error("Failed to fetch pages:", err);
      }
    };

    fetchPages();
  }, []);

  const getInfo = async (): Promise<{ clientId: string; key: string, api: string }> => {
    const { clientId, key, api } = await getDoc(doc(db, "config", "facebook")).then(
      (docSnap) => ({ ...docSnap.data() }),
    );

    if (!clientId || clientId === "") {
      console.error("Facebook Client ID not found");
      return { clientId: "", key: "", api: "" };
    }

    if (!key || key === "") {
      console.error("Facebook Server Auth Token not found");
      return { clientId: "", key: "", api: "" };
    }

    return { clientId, key, api };
  };

  useEffect(() => {
    getInfo()
      .then(async ({ clientId, key, api }) => {
        setClientId(clientId);
        setServerAuthToken(key);
        setApiEndpoint(api);

        if (key.length > 0) {
          const res = await fetch(`${api}/ping`, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${key}`,
              Referer: window.location.origin,
            },
          });
          if (res.ok) {
            console.log("Successfully authenticated with server config key");
          } else {
            console.error("Failed to authenticate with server config key");
          }
        }
      })
      .catch((e) => {
        console.error("Error fetching Facebook config:", e);
      });

    return () => {
      // Clean up any stored auth data on unmount
      setClientId("");
      setServerAuthToken("");
    };
  }, []);

  // 2. Handle File Selection
  const handleFileChange = (e: any) => {
    if (e.target.files && e.target.files[0]) {
      setMedia(e.target.files[0]);
    }
  };

  // 3. Handle Form Submission to your Proxy
  const handleSubmit = async (e: any) => {
    e.preventDefault();
    if (!message && !media) return;

    setStatus("loading");
    setFeedback("");

    try {
      const formData = new FormData();
      formData.append("pageId", selectedPage);
      formData.append("message", message);
      if (media) formData.append("media", media);

      // TODO: Replace with your actual proxy server POST endpoint
      /*
      const response = await fetch('/api/publish-to-facebook', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) throw new Error('Failed to publish post');
      */

      // Simulating network request
      await new Promise((resolve) => setTimeout(resolve, 1500));

      setStatus("success");
      setFeedback("Post published successfully!");
      setMessage("");
      setMedia(null);

      // Reset success message after 3 seconds
      setTimeout(() => setStatus("idle"), 3000);
    } catch (err) {
      setStatus("error");
      setFeedback("Error publishing post. Check server logs.");
    }
  };

  const loginToFacebook = async () => {
    if (!clientId || clientId === "") {
      console.error("Facebook Client ID not found in Firestore");
      return;
    }

    //const clientId = '4534613633449277';
    const redirectUri = "https://scryptworxstudios.com/callback.html";
    const scope =
      "pages_show_list,business_management,pages_read_engagement,pages_manage_metadata,pages_read_user_content,pages_manage_posts,pages_manage_engagement";

    //const loginURL = `https://www.facebook.com/v25.0/dialog/oauth?response_type=token&display=popup&client_id=4534613633449277&redirect_uri=https://scryptworxstudios.com/callback.html?client_id=4534613633449277&scope=pages_show_list,business_management,pages_read_engagement,pages_manage_metadata,pages_read_user_content,pages_manage_posts,pages_manage_engagement`
    const authUrl =
      `https://www.facebook.com/v25.0/dialog/oauth` +
      `?client_id=${clientId}` +
      `&redirect_uri=${redirectUri}` +
      `&scope=${scope}` +
      `&response_type=code` +
      `&auth_type=rerequest`;

    window.open(authUrl, "_blank");
  };

  const upgradeToken = async () => {
    const endpoint = await getDoc(doc(db, "config", "facebook")).then(
      (docSnap) => ({
        endpoint: docSnap.data()?.upgradeEndpoint || "",
        serverAuthToken: docSnap.data()?.serverAuthToken || "",
      }),
    );

    if (
      !endpoint ||
      !endpoint.endpoint ||
      endpoint.endpoint === "" ||
      !endpoint.serverAuthToken ||
      endpoint.serverAuthToken === ""
    ) {
      console.error("Facebook Upgrade Endpoint not found in Firestore");
      return;
    }

    // Get values from sessionStorage
    const code = sessionStorage.getItem("facebook_code");
    const state = sessionStorage.getItem("facebook_state");

    if (!code) {
      throw new Error("Missing Facebook auth code in sessionStorage");
    }

    const response = await fetch(endpoint.endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${endpoint.serverAuthToken}`,
      },
      credentials: "include",
      body: JSON.stringify({
        code,
        state,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data?.error || "Failed to send auth data");
    }

    return data;
  };

  const refreshPagesList = () => {
    // Re-run your fetchPages() function here to grab the newly added pages from your backend
    console.log("Refreshing page list from server...");
  };

  return (
    <div className="flex flex-col bg-[#19181c] text-white px-6 w-full py-16 min-h-screen">
      {/* 3. Mount the Modal outside the main flow */}
      <AddPageModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={refreshPagesList}
      />
      <div className="max-w-4xl mx-auto h-full w-full">
        {/* Header */}
        <div className="mb-12 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <h1 className="text-5xl font-bold mb-4 text-[#F6B34B]">
              Facebook Page Manager
            </h1>
            <p className="text-white/70 text-sm">
              Publish content to your connected Facebook Pages via the proxy
              server.
            </p>
          </div>
          <button
            type="button"
            onClick={() => loginToFacebook()}
            className="flex items-center justify-center px-5 py-2.5 rounded-lg text-sm font-bold text-white bg-white/10 hover:bg-white/20 border border-white/20 transition-all"
          >
            <svg
              className="w-5 h-5 mr-2 text-[#F6B34B]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              ></path>
            </svg>
            Login to Facebook
          </button>
          {/* 4. The trigger button */}
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            className="flex items-center justify-center px-5 py-2.5 rounded-lg text-sm font-bold text-white bg-white/10 hover:bg-white/20 border border-white/20 transition-all"
          >
            <svg
              className="w-5 h-5 mr-2 text-[#F6B34B]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              ></path>
            </svg>
            ADD NEW PAGE
          </button>
        </div>

        {/* Main Card */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6 sm:p-8 shadow-2xl">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Page Selector */}
            <div>
              <label
                htmlFor="page"
                className="block text-sm font-semibold mb-3 text-[#F6B34B]"
              >
                Select Target Page
              </label>
              <select
                id="page"
                value={selectedPage}
                onChange={(e) => setSelectedPage(e.target.value)}
                className="block w-full pl-4 pr-10 py-3 text-base border border-white/20 focus:outline-none focus:ring-1 focus:ring-[#F6B34B] focus:border-[#F6B34B] sm:text-sm rounded-lg bg-[#19181c] text-white/90 transition-colors"
              >
                {pages.map((page: any) => (
                  <option key={page.id} value={page.id}>
                    {page.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Message Input */}
            <div>
              <label
                htmlFor="message"
                className="block text-sm font-semibold mb-3 text-[#F6B34B]"
              >
                Post Content
              </label>
              <textarea
                id="message"
                rows={5}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="What's on your mind?"
                className="block w-full sm:text-sm border border-white/20 focus:ring-1 focus:ring-[#F6B34B] focus:border-[#F6B34B] rounded-lg p-4 bg-[#19181c] text-white/90 placeholder-white/30 resize-none shadow-inner transition-colors"
              />
            </div>

            {/* Media Upload */}
            <div>
              <label className="block text-sm font-semibold mb-3 text-[#F6B34B]">
                Attach Media (Optional)
              </label>
              <div className="mt-2 flex items-center justify-center w-full">
                <label
                  htmlFor="dropzone-file"
                  className="flex flex-col items-center justify-center w-full h-32 border-2 border-white/20 border-dashed rounded-lg cursor-pointer bg-white/5 hover:bg-white/10 transition-colors"
                >
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <svg
                      className="w-8 h-8 mb-3 text-white/50"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                      ></path>
                    </svg>
                    <p className="mb-2 text-sm text-white/70">
                      <span className="font-semibold text-[#F6B34B]">
                        Click to upload
                      </span>{" "}
                      or drag and drop
                    </p>
                    <p className="text-xs text-white/40">PNG, JPG or MP4</p>
                  </div>
                  <input
                    id="dropzone-file"
                    type="file"
                    className="hidden"
                    accept="image/*,video/*"
                    onChange={handleFileChange}
                  />
                </label>
              </div>

              {media && (
                <div className="mt-4 flex items-center justify-between p-3 bg-white/10 border border-white/20 text-white/90 rounded-lg text-sm">
                  <span className="truncate max-w-[200px] sm:max-w-xs flex items-center gap-2">
                    <svg
                      className="w-4 h-4 text-[#F6B34B]"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8 4a3 3 0 00-3 3v4a5 5 0 0010 0V7a1 1 0 112 0v4a7 7 0 11-14 0V7a5 5 0 0110 0v4a3 3 0 11-6 0V7a1 1 0 012 0v4a1 1 0 102 0V7a3 3 0 00-3-3z"
                        clipRule="evenodd"
                      ></path>
                    </svg>
                    {media.name}
                  </span>
                  <button
                    type="button"
                    onClick={() => setMedia(null)}
                    className="text-[#F6B34B] hover:text-[#F6B34B]/80 font-medium transition-colors"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>

            {/* Status Feedback */}
            {status === "success" && (
              <div className="p-4 rounded-lg bg-[#2ecc71]/10 border border-[#2ecc71]/30">
                <p className="text-sm text-[#2ecc71] flex items-center font-medium">
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    ></path>
                  </svg>
                  {feedback}
                </p>
              </div>
            )}

            {status === "error" && (
              <div className="p-4 rounded-lg bg-[#e74c3c]/10 border border-[#e74c3c]/30">
                <p className="text-sm text-[#e74c3c] flex items-center font-medium">
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    ></path>
                  </svg>
                  {feedback}
                </p>
              </div>
            )}

            {/* Submit Button */}
            <div className="pt-2">
              <button
                type="submit"
                disabled={status === "loading" || (!message && !media)}
                className="w-full flex justify-center py-4 px-4 rounded-lg shadow-sm text-sm font-bold text-[#19181c] bg-[#F6B34B] hover:bg-[#F6B34B]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F6B34B] focus:ring-offset-[#19181c] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 uppercase tracking-wide"
              >
                {status === "loading" ? (
                  <span className="flex items-center">
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-[#19181c]"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Publishing...
                  </span>
                ) : (
                  "Publish Post"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MetaManager;
